import Map from 'ol/Map';
import { fromLonLat } from 'ol/proj';
import { easeOut } from 'ol/easing';
import type { Layer } from '../store/slices/layersSlice';
import type { SelectedFeature } from '../store/slices/selectionSlice';

/**
 * Direction for pan operations.
 */
export type PanDirection = 'north' | 'south' | 'east' | 'west';

/**
 * Configuration constants for map navigation.
 */
export const NAVIGATION_CONFIG = {
  /** Minimum allowed zoom level */
  MIN_ZOOM: 2,
  /** Maximum allowed zoom level */
  MAX_ZOOM: 20,
  /** Animation duration in milliseconds */
  ANIMATION_DURATION: 300,
  /** Pixels to pan per keyboard arrow press */
  PAN_DISTANCE_PX: 100,
  /** Padding in pixels for extent zoom operations */
  EXTENT_PADDING: 50,
  /** Default world extent in EPSG:4326 [minLon, minLat, maxLon, maxLat] */
  DEFAULT_WORLD_EXTENT: [-180, -85, 180, 85] as [number, number, number, number],
} as const;

/**
 * MapNavigator service provides reusable map navigation methods including
 * zoom, pan, and extent management for the GeoVera WebGIS platform.
 *
 * This service wraps OpenLayers View operations with consistent animation
 * and constraint enforcement.
 *
 * **Validates: Requirements 30.1, 30.2, 30.3, 30.4, 30.5, 30.6, 31.1, 31.2, 31.3, 31.4, 31.5, 32.1, 32.2, 32.3, 32.4**
 */
export class MapNavigator {
  private map: Map | null = null;

  /**
   * Attach the navigator to an OpenLayers Map instance.
   */
  setMap(map: Map | null): void {
    this.map = map;
  }

  /**
   * Get the currently attached map instance.
   */
  getMap(): Map | null {
    return this.map;
  }

  /**
   * Zoom in by one level with smooth animation.
   * Respects max zoom constraint.
   *
   * **Validates: Requirements 30.1, 30.2, 30.6**
   */
  zoomIn(): void {
    const view = this.map?.getView();
    if (!view) return;

    const currentZoom = view.getZoom();
    if (currentZoom === undefined) return;

    const targetZoom = Math.min(currentZoom + 1, NAVIGATION_CONFIG.MAX_ZOOM);
    view.animate({
      zoom: targetZoom,
      duration: NAVIGATION_CONFIG.ANIMATION_DURATION,
      easing: easeOut,
    });
  }

  /**
   * Zoom out by one level with smooth animation.
   * Respects min zoom constraint.
   *
   * **Validates: Requirements 30.1, 30.2, 30.6**
   */
  zoomOut(): void {
    const view = this.map?.getView();
    if (!view) return;

    const currentZoom = view.getZoom();
    if (currentZoom === undefined) return;

    const targetZoom = Math.max(currentZoom - 1, NAVIGATION_CONFIG.MIN_ZOOM);
    view.animate({
      zoom: targetZoom,
      duration: NAVIGATION_CONFIG.ANIMATION_DURATION,
      easing: easeOut,
    });
  }

  /**
   * Zoom to a specific level with smooth animation.
   * Clamps to min/max constraints.
   *
   * **Validates: Requirements 30.5, 30.6**
   */
  zoomToLevel(level: number): void {
    const view = this.map?.getView();
    if (!view) return;

    const clampedLevel = Math.max(
      NAVIGATION_CONFIG.MIN_ZOOM,
      Math.min(level, NAVIGATION_CONFIG.MAX_ZOOM)
    );

    view.animate({
      zoom: clampedLevel,
      duration: NAVIGATION_CONFIG.ANIMATION_DURATION,
      easing: easeOut,
    });
  }

  /**
   * Fly to a lon/lat coordinate (EPSG:4326) at the given zoom.
   */
  zoomToLonLat(lon: number, lat: number, zoom = 16): void {
    const view = this.map?.getView();
    if (!view) return;

    const clampedZoom = Math.max(
      NAVIGATION_CONFIG.MIN_ZOOM,
      Math.min(zoom, NAVIGATION_CONFIG.MAX_ZOOM)
    );

    view.animate({
      center: fromLonLat([lon, lat]),
      zoom: clampedZoom,
      duration: NAVIGATION_CONFIG.ANIMATION_DURATION,
      easing: easeOut,
    });
  }

  /**
   * Zoom to a given extent (in EPSG:3857) with padding and animation.
   *
   * **Validates: Requirements 32.1, 32.2, 32.4**
   */
  zoomToExtent(
    extent: [number, number, number, number],
    options?: {
      /** [top, right, bottom, left] padding in px */
      padding?: [number, number, number, number];
      duration?: number;
      maxZoom?: number;
    }
  ): void {
    const view = this.map?.getView();
    if (!view || !this.map) return;

    try {
      this.map.updateSize();
    } catch {
      /* ignore */
    }

    const pad = options?.padding;
    const p = pad
      ? pad
      : ([
          NAVIGATION_CONFIG.EXTENT_PADDING,
          NAVIGATION_CONFIG.EXTENT_PADDING,
          NAVIGATION_CONFIG.EXTENT_PADDING,
          NAVIGATION_CONFIG.EXTENT_PADDING,
        ] as [number, number, number, number]);

    // Expand tiny extents (single point / small parcel) so fit has room
    let fitExtent = extent;
    const w = extent[2] - extent[0];
    const h = extent[3] - extent[1];
    const minSpan = 40; // metres-ish in 3857 for very small polygons
    if (w < minSpan || h < minSpan) {
      const cx = (extent[0] + extent[2]) / 2;
      const cy = (extent[1] + extent[3]) / 2;
      const half = Math.max(minSpan, w, h) / 2;
      fitExtent = [cx - half, cy - half, cx + half, cy + half];
    }

    view.fit(fitExtent, {
      size: this.map.getSize(),
      padding: p,
      duration: options?.duration ?? NAVIGATION_CONFIG.ANIMATION_DURATION,
      maxZoom: options?.maxZoom ?? NAVIGATION_CONFIG.MAX_ZOOM,
      easing: easeOut,
    });
  }

  /**
   * Zoom to the combined extent of all visible layers.
   * Falls back to default world extent when no layers are visible or have no features.
   *
   * **Validates: Requirements 32.1, 32.2, 32.3, 32.4**
   */
  zoomToAllLayers(
    layers: Layer[],
    options?: {
      padding?: [number, number, number, number];
      duration?: number;
      maxZoom?: number;
    }
  ): void {
    const extent = this.calculateAllLayersExtent(layers);
    if (extent) {
      this.zoomToExtent(extent, options);
    } else {
      // No visible layers or no features — zoom to default world extent
      this.zoomToDefaultExtent();
    }
  }

  /**
   * Wait until map is attached and has a non-zero size, then zoom to layers.
   * Used by public share pages where Map mounts after data loads.
   */
  async zoomToAllLayersWhenReady(
    layers: Layer[],
    options?: {
      padding?: [number, number, number, number];
      duration?: number;
      maxZoom?: number;
      /** max wait attempts (default 40 × 50ms ≈ 2s) */
      attempts?: number;
    }
  ): Promise<boolean> {
    const maxAttempts = options?.attempts ?? 40;
    for (let i = 0; i < maxAttempts; i++) {
      const map = this.map;
      const size = map?.getSize();
      if (map && size && size[0] > 0 && size[1] > 0) {
        this.zoomToAllLayers(layers, options);
        // Second fit after layout settles (banners / mobile chrome)
        await new Promise((r) => setTimeout(r, 120));
        this.zoomToAllLayers(layers, options);
        return true;
      }
      await new Promise((r) => setTimeout(r, 50));
    }
    // Last attempt even if size is still 0
    if (this.map) {
      this.zoomToAllLayers(layers, options);
      return true;
    }
    return false;
  }

  /**
   * Zoom to the extent of a single layer by its ID.
   * Does nothing if the layer has no features.
   *
   * **Validates: Requirement 32.1**
   */
  zoomToLayer(layerId: string, layers: Layer[]): void {
    const layer = layers.find((l) => l.id === layerId);
    if (!layer) return;

    const extent = this.calculateLayerExtent(layer);
    if (extent) {
      this.zoomToExtent(extent);
    }
  }

  /**
   * Zoom to the extent of selected features.
   * Does nothing if no features are selected or features have no geometry.
   *
   * **Validates: Requirement 32.1**
   */
  zoomToSelection(selectedFeatures: SelectedFeature[], layers: Layer[]): void {
    const extent = this.calculateSelectionExtent(selectedFeatures, layers);
    if (extent) {
      this.zoomToExtent(extent);
    }
  }

  /**
   * Pan the map in a cardinal direction by a fixed pixel distance.
   * Maintains the current zoom level during pan.
   *
   * **Validates: Requirements 31.4, 31.5**
   */
  pan(direction: PanDirection): void {
    const view = this.map?.getView();
    if (!view || !this.map) return;

    const resolution = view.getResolution();
    if (resolution === undefined) return;

    const center = view.getCenter();
    if (!center) return;

    // Convert pixel distance to map units
    const distance = NAVIGATION_CONFIG.PAN_DISTANCE_PX * resolution;

    let newCenter: [number, number];
    switch (direction) {
      case 'north':
        newCenter = [center[0], center[1] + distance];
        break;
      case 'south':
        newCenter = [center[0], center[1] - distance];
        break;
      case 'east':
        newCenter = [center[0] + distance, center[1]];
        break;
      case 'west':
        newCenter = [center[0] - distance, center[1]];
        break;
    }

    view.animate({
      center: newCenter,
      duration: NAVIGATION_CONFIG.ANIMATION_DURATION,
      easing: easeOut,
    });
  }

  /**
   * Zoom to the default world extent.
   *
   * **Validates: Requirement 32.3**
   */
  zoomToDefaultExtent(): void {
    const [minLon, minLat, maxLon, maxLat] = NAVIGATION_CONFIG.DEFAULT_WORLD_EXTENT;
    const bottomLeft = fromLonLat([minLon, minLat]);
    const topRight = fromLonLat([maxLon, maxLat]);
    const extent: [number, number, number, number] = [
      bottomLeft[0],
      bottomLeft[1],
      topRight[0],
      topRight[1],
    ];
    this.zoomToExtent(extent);
  }

  /**
   * Calculate the combined extent of all visible layers with features.
   * Returns null if no visible layers have features.
   */
  calculateAllLayersExtent(layers: Layer[]): [number, number, number, number] | null {
    const visibleLayers = layers.filter((l) => l.visible && l.features && l.features.length > 0);
    if (visibleLayers.length === 0) return null;

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    for (const layer of visibleLayers) {
      const layerExtent = this.calculateLayerExtent(layer);
      if (layerExtent) {
        minX = Math.min(minX, layerExtent[0]);
        minY = Math.min(minY, layerExtent[1]);
        maxX = Math.max(maxX, layerExtent[2]);
        maxY = Math.max(maxY, layerExtent[3]);
      }
    }

    if (minX === Infinity) return null;
    return [minX, minY, maxX, maxY];
  }

  /**
   * Calculate the extent of a single layer from its features.
   * Returns coordinates in EPSG:3857.
   * Returns null if the layer has no features.
   */
  calculateLayerExtent(layer: Layer): [number, number, number, number] | null {
    if (!layer.features || layer.features.length === 0) return null;

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    for (const feature of layer.features) {
      const coords = this.extractCoordinates(feature.geometry);
      for (const [lon, lat] of coords) {
        const projected = fromLonLat([lon, lat]);
        minX = Math.min(minX, projected[0]);
        minY = Math.min(minY, projected[1]);
        maxX = Math.max(maxX, projected[0]);
        maxY = Math.max(maxY, projected[1]);
      }
    }

    if (minX === Infinity) return null;
    return [minX, minY, maxX, maxY];
  }

  /**
   * Calculate the extent of selected features.
   * Returns coordinates in EPSG:3857.
   * Returns null if no features are found.
   */
  calculateSelectionExtent(
    selectedFeatures: SelectedFeature[],
    layers: Layer[]
  ): [number, number, number, number] | null {
    if (selectedFeatures.length === 0) return null;

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    for (const selected of selectedFeatures) {
      const layer = layers.find((l) => l.id === selected.layerId);
      if (!layer?.features) continue;

      const feature = layer.features.find((f) => f.id === selected.featureId);
      if (!feature) continue;

      const coords = this.extractCoordinates(feature.geometry);
      for (const [lon, lat] of coords) {
        const projected = fromLonLat([lon, lat]);
        minX = Math.min(minX, projected[0]);
        minY = Math.min(minY, projected[1]);
        maxX = Math.max(maxX, projected[0]);
        maxY = Math.max(maxY, projected[1]);
      }
    }

    if (minX === Infinity) return null;
    return [minX, minY, maxX, maxY];
  }

  /**
   * Extract all coordinate pairs from a GeoJSON geometry.
   * Returns an array of [longitude, latitude] pairs.
   */
  private extractCoordinates(geometry: GeoJSON.Geometry): [number, number][] {
    const coords: [number, number][] = [];

    switch (geometry.type) {
      case 'Point':
        coords.push(geometry.coordinates as [number, number]);
        break;
      case 'MultiPoint':
      case 'LineString':
        for (const coord of geometry.coordinates) {
          coords.push(coord as [number, number]);
        }
        break;
      case 'MultiLineString':
      case 'Polygon':
        for (const ring of geometry.coordinates) {
          for (const coord of ring) {
            coords.push(coord as [number, number]);
          }
        }
        break;
      case 'MultiPolygon':
        for (const polygon of geometry.coordinates) {
          for (const ring of polygon) {
            for (const coord of ring) {
              coords.push(coord as [number, number]);
            }
          }
        }
        break;
      case 'GeometryCollection':
        for (const geom of geometry.geometries) {
          coords.push(...this.extractCoordinates(geom));
        }
        break;
    }

    return coords;
  }
}

/**
 * Singleton instance of the MapNavigator service.
 * Components and hooks should use this shared instance.
 */
export const mapNavigator = new MapNavigator();
